import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { AuthShell, AuthCard, AuthInput, AuthButton, AuthError } from '../components/AuthShell';
import { prefetchChallenge, buildHumanCheck, clearChallenge } from '../lib/pow';

const REFERRAL_KEY = 'squishy_pending_referral';

export function SignUpPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, loading, error, clearError } = useAuthStore();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Honeypot: hidden from humans; bots that autofill it are rejected server-side.
  const [website, setWebsite] = useState('');
  const returnUrl = searchParams.get('returnUrl') ?? '';
  const joinMatch = returnUrl.match(/\/groups\/join\/([A-Z0-9]+)/i);
  const joinRoomCode = joinMatch?.[1] ?? null;

  // Capture referral param from URL and persist in localStorage
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem(REFERRAL_KEY, ref);
  }, [searchParams]);

  // Warm up the bot-protection challenge while the user fills the form, so the
  // minimum challenge age (3s server-side) has usually elapsed by submit time.
  useEffect(() => { prefetchChallenge(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const cleanEmail = email.trim().toLowerCase();
    // If the challenge can't be fetched/solved, still attempt the signup; the
    // server decides. Never block a user on a client-side hiccup.
    const humanCheck = await buildHumanCheck(cleanEmail).catch(() => '');
    const r = await signUp(cleanEmail, password, firstName.trim(), lastName.trim(), { website, humanCheck });
    if (r.ok) {
      if (r.needsConfirm) nav('/confirm', { state: { email, returnUrl: returnUrl || undefined } });
      else nav(returnUrl || '/');
    } else {
      clearChallenge(); // retry gets a fresh challenge
    }
  };

  return (
    <AuthShell>
      <AuthCard
        title={joinRoomCode ? 'Sign up to join' : 'Create your account'}
        subtitle={joinRoomCode ? undefined : 'Free to start. No credit card required.'}
      >
        {joinRoomCode && (
          <div className="mb-5 rounded-2xl border border-teal-200 bg-teal-50 p-4 text-center">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">Study group invite</div>
            <div className="mt-1 font-mono text-xl font-bold tracking-[0.25em] text-squishy-navy">{joinRoomCode}</div>
            <div className="mt-1 text-xs text-slate-500">Create an account and you'll join automatically</div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          {/* Honeypot: kept off-screen (not display:none — some bots skip those).
              Real users never see or fill it. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="signup-website">Website</label>
            <input
              id="signup-website"
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <AuthInput label="First name" value={firstName} onChange={setFirstName} autoComplete="given-name" required autoFocus />
            <AuthInput label="Last name"  value={lastName}  onChange={setLastName}  autoComplete="family-name" required />
          </div>
          <AuthInput type="email" label="Email" value={email} onChange={setEmail} autoComplete="email" required />
          <AuthInput
            type="password" label="Password" value={password} onChange={setPassword}
            autoComplete="new-password" required
            placeholder="At least 10 chars, with upper/lower/number"
          />
          {error && <AuthError message={error} />}
          <AuthButton type="submit" loading={loading}>Create account</AuthButton>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Already have an account? <Link to={`/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} className="inline-flex min-h-[44px] items-center font-medium text-squishy-primary transition-colors hover:text-squishy-navy hover:underline">Sign in</Link>
        </p>
        <p className="mt-1 text-center text-xs leading-relaxed text-slate-400">
          By creating an account, you agree to our <Link to="/privacy" className="transition-colors hover:text-squishy-primary hover:underline">Privacy Policy</Link>.
        </p>
      </AuthCard>
    </AuthShell>
  );
}
